
import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from "@/components/ui/button";
import { Zap, Shield, Recycle, TrendingUp, AlertTriangle, CheckCircle } from 'lucide-react';
import ValorisationPageLayout from '@/components/valorisation/ValorisationPageLayout';
import ValorisationContentSection from '@/components/valorisation/ValorisationContentSection';

const DEEE = () => {
  const structuredData = {
    "@context": "https://schema.org",
    "@type": "Service",
    "name": "Recyclage DEEE - Déchets d'Équipements Électriques et Électroniques",
    "description": "Service professionnel de collecte et recyclage des DEEE avec traçabilité complète et dépollution sécurisée.",
    "provider": {
      "@type": "Organization",
      "name": "Groupe Fabre Environnement",
      "url": "https://www.groupefabre-environnement.fr"
    },
    "areaServed": ["Occitanie", "Auvergne-Rhône-Alpes", "Île-de-France"],
    "serviceType": "Recyclage DEEE",
    "offers": {
      "@type": "Offer",
      "description": "Collecte et recyclage de tous types de DEEE"
    }
  };

  const breadcrumbItems = [
    { label: "Accueil", href: "/" },
    { label: "Valorisation", href: "/valorisation" },
    { label: "DEEE", href: "/valorisation/deee", current: true }
  ];

  const contentBlocks = [
    {
      type: 'text' as const,
      title: "Qu'est-ce que les DEEE ?",
      content: "Les DEEE (Déchets d'Équipements Électriques et Électroniques) sont tous les équipements fonctionnant grâce à des courants électriques ou des champs électromagnétiques arrivés en fin de vie. Leur recyclage est obligatoire car ils contiennent des substances dangereuses mais aussi des matériaux précieux récupérables.",
      variant: 'highlighted' as const
    },
    {
      type: 'list' as const,
      title: "Types de DEEE que nous recyclons",
      items: [
        "Gros électroménager : réfrigérateurs, lave-linge, lave-vaisselle",
        "Petit électroménager : aspirateurs, micro-ondes, grille-pain",
        "Équipements informatiques : ordinateurs, imprimantes, serveurs",
        "Équipements de télécommunication : téléphones, fax, routeurs",
        "Matériel audiovisuel : téléviseurs, chaînes hi-fi, lecteurs DVD",
        "Outils électriques : perceuses, scies, ponceuses",
        "Jouets électroniques et équipements de loisirs",
        "Dispositifs médicaux électroniques"
      ]
    },
    {
      type: 'process' as const,
      title: "Notre processus de traitement des DEEE",
      process: [
        {
          step: 1,
          title: "Collecte sécurisée",
          description: "Collecte des DEEE avec respect des normes de sécurité et de transport"
        },
        {
          step: 2,
          title: "Dépollution",
          description: "Extraction des substances dangereuses (métaux lourds, gaz réfrigérants, etc.)"
        },
        {
          step: 3,
          title: "Démantèlement",
          description: "Séparation des différents composants et matériaux"
        },
        {
          step: 4,
          title: "Tri et valorisation",
          description: "Récupération des métaux précieux, plastiques et autres matériaux valorisables"
        },
        {
          step: 5,
          title: "Traçabilité",
          description: "Émission de certificats de destruction et de recyclage"
        }
      ]
    },
    {
      type: 'benefits' as const,
      title: "Pourquoi recycler les DEEE ?",
      benefits: [
        {
          title: "Conformité réglementaire",
          description: "Respect des obligations légales et des directives européennes sur les DEEE",
          icon: Shield
        },
        {
          title: "Protection environnementale",
          description: "Éviter la pollution par les substances dangereuses et récupérer les matières premières",
          icon: Recycle
        },
        {
          title: "Valorisation économique",
          description: "Récupération de métaux précieux et matériaux à haute valeur ajoutée",
          icon: TrendingUp
        }
      ],
      variant: 'success' as const
    },
    {
      type: 'cta' as const,
      title: "Confiez-nous vos DEEE en toute sécurité",
      content: "Notre expertise garantit un traitement conforme et une traçabilité complète de vos déchets électroniques.",
      ctaText: "Demander une collecte",
      ctaLink: "/contact",
      variant: 'highlighted' as const
    }
  ];

  return (
    <ValorisationPageLayout
      title="Recyclage DEEE - Déchets Électriques et Électroniques | Groupe Fabre"
      description="Collecte et recyclage professionnel des DEEE avec traçabilité complète. Conformité réglementaire et valorisation des déchets électroniques."
      canonical="/valorisation/deee"
      keywords="recyclage DEEE, déchets électroniques, équipements électriques, dépollution, traçabilité"
      structuredData={structuredData}
      heroTitle="DEEE"
      heroSubtitle="Recyclage professionnel des déchets d'équipements électriques et électroniques"
      breadcrumbItems={breadcrumbItems}
    >
      <ValorisationContentSection
        title="Recyclage spécialisé des DEEE"
        subtitle="Traitement conforme et sécurisé de vos déchets d'équipements électriques et électroniques"
        blocks={contentBlocks}
        className="bg-white"
      />
    </ValorisationPageLayout>
  );
};

export default DEEE;
